import {
  GAMES_BROWSE_PATH,
  isLegacyGamesBrowseQueryUrl,
  parseGamesBrowsePath,
} from '../games/shared/gamesBrowseUrl';
import { parseCorpusPathSegment } from '../common/gameSource';
import { blackFamilyHubTitle, whiteCollectionHubTitle } from '../courses/courseHelpers';
import { parseWhiteCollectionParam } from '../courses/coursesUrl';
import { isBlackBrowseFamilySlug } from 'endchess-contracts';
import {
  formatPlayerSerpDisplayName,
  formatPlayerSerpPossessive,
} from '../games/shared/playerName';

export const SITE_NAME = 'Endchess';

export const SITE_URL = (
  process.env.REACT_APP_SITE_URL ?? 'https://endchess.com'
).replace(/\/$/, '');

const ogImagePath = process.env.REACT_APP_OG_IMAGE ?? '/endchess-logo.png';

export const OG_IMAGE_URL = ogImagePath.startsWith('http')
  ? ogImagePath
  : `${SITE_URL}${ogImagePath.startsWith('/') ? ogImagePath : `/${ogImagePath}`}`;

export const ROBOTS_NOINDEX = process.env.REACT_APP_ROBOTS_NOINDEX === '1';

export type PageSeo = {
  title: string;
  description: string;
  canonicalPath: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const breadcrumbJsonLd = (
  items: Array<{ name: string; path: string }>,
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

const websiteJsonLd = (): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/games/browse?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
});

const courseJsonLd = (
  title: string,
  description: string,
  path: string,
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: title,
  description,
  url: absoluteUrl(path),
  provider: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
  },
});

export const HOME_SEO: PageSeo = {
  title: 'Chess Training from Elite Games',
  description:
    'Replay elite games move by move, drill your misses, and review with spaced repetition. Study openings, middlegames, and endgames on Endchess.',
  canonicalPath: '/',
};

export const SUBSCRIBE_SEO: PageSeo = {
  title: 'Pricing',
  description:
    'Start free with a 90-day full-access trial, then choose Free limits or Pro for unlimited puzzles, review, courses, explorer, and replay on Endchess.',
  canonicalPath: '/subscribe',
};

export const CONTACT_SEO: PageSeo = {
  title: 'Contact',
  description:
    'Contact Endchess for bugs, feature requests, or questions about training, subscriptions, and your account.',
  canonicalPath: '/contact',
};

export const EXPLORER_SEO: PageSeo = {
  title: 'Opening Explorer',
  description:
    'Explore opening positions from elite games. See what strong players play, how often, and how lines score on Endchess.',
  canonicalPath: '/explorer',
};

export const EXPLORER_PAGE_HEADING = EXPLORER_SEO.title;

export const EXPLORER_OPENINGS_HUB_PATH = '/explorer/openings';

export const EXPLORER_OPENINGS_HUB_SEO: PageSeo = {
  title: 'Chess Openings Explorer',
  description:
    'Browse chess openings. Open any variation to explore elite games. Study and review with spaced repetition on Endchess.',
  canonicalPath: EXPLORER_OPENINGS_HUB_PATH,
};

export const explorerOpeningSeoTitle = (
  openingTitle: string,
  eco?: string,
): string => {
  const ecoPart = eco ? ` (${eco.toUpperCase()})` : '';
  return `${openingTitle}${ecoPart} — Moves & Elite Games`;
};

export const explorerOpeningSeoDescription = (
  openingTitle: string,
  eco?: string,
): string => {
  const name = eco
    ? `${openingTitle} (${eco.toUpperCase()})`
    : openingTitle;
  return `Explore the ${name} with move stats and elite master/Lichess games. See popular continuations and how lines score on Endchess.`;
};

export const COURSES_HUB_SEO: PageSeo = {
  title: 'Chess Courses',
  description:
    'Train opening, middlegame, and endgame courses built from elite games. Study lines move by move and review with spaced repetition on Endchess.',
  canonicalPath: '/courses',
};

export const GAMES_BROWSE_HUB_SEO: PageSeo = {
  title: 'Browse Elite Chess Games',
  description:
    'Search elite games by opening or player. Replay any game move by move and review with spaced repetition on Endchess.',
  canonicalPath: GAMES_BROWSE_PATH,
};

const COURSE_PHASE_SEO: Record<string, PageSeo> = {
  opening: {
    title: 'Opening Courses',
    description:
      'Train opening lines from structured courses. Study them move by move and review with spaced repetition on Endchess.',
    canonicalPath: '/courses/opening',
  },
  middlegame: {
    title: 'Middlegame Courses',
    description:
      'Study middlegame plans from elite games. Drill typical positions and review with spaced repetition on Endchess.',
    canonicalPath: '/courses/middlegame',
  },
  endgame: {
    title: 'Endgame Courses',
    description:
      'Drill endgame technique from elite games. Study key positions move by move and review with spaced repetition on Endchess.',
    canonicalPath: '/courses/endgame',
  },
  mistakes: {
    title: 'My Mistakes',
    description:
      'Review and train positions from your own games with spaced repetition.',
    canonicalPath: '/courses/mistakes',
  },
};

export const isExplorerOpeningsSeoPath = (pathname: string): boolean =>
  pathname === EXPLORER_OPENINGS_HUB_PATH ||
  pathname.startsWith(`${EXPLORER_OPENINGS_HUB_PATH}/`);

const PRIVATE_ROUTE_PREFIXES = [
  '/dashboard',
  '/stats',
  '/settings',
  '/review',
  '/puzzles',
  '/replay',
  '/verify-email',
  '/forgot-password',
  '/reset-password',
  '/login-success',
  '/login-failed',
];

const slugToDisplayTitle = (slug: string): string =>
  slug
    .split('-')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const isPrivateRoute = (pathname: string): boolean => {
  if (pathname === '/games/mine' || pathname.startsWith('/games/mine/')) {
    return true;
  }
  if (pathname === '/courses/train' || pathname.endsWith('/train')) {
    return true;
  }
  return PRIVATE_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
};

export const resolveCanonicalPath = (pathname: string): string => {
  if (pathname === '/login') {
    return '/';
  }
  return pathname || '/';
};

export const absoluteUrl = (path: string): string => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalized}`;
};

export const formatPageTitle = (title: string): string => {
  if (title === SITE_NAME) {
    return SITE_NAME;
  }
  return `${title} • ${SITE_NAME}`;
};

const hasExplorerMovesQuery = (search: string): boolean => {
  const query = search.startsWith('?') ? search.slice(1) : search;
  const movesParam = new URLSearchParams(query).get('moves');
  if (!movesParam?.trim()) {
    return false;
  }
  return movesParam.split(',').some((move) => move.trim());
};

/** Only the root explorer landing (no move line in the URL) is indexable. */
export const isIndexableExplorerLanding = (
  pathname: string,
  search = '',
): boolean => pathname === '/explorer' && !hasExplorerMovesQuery(search);

const getCoursesRouteSeo = (pathname: string): PageSeo | null => {
  if (pathname === '/courses/train' || pathname.endsWith('/train')) {
    return null;
  }

  if (pathname === '/courses') {
    return COURSES_HUB_SEO;
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'courses' || segments.length < 2) {
    return null;
  }

  const phase = segments[1];
  const phaseSeo = COURSE_PHASE_SEO[phase];
  if (!phaseSeo) {
    return {
      title: 'Courses',
      description: COURSES_HUB_SEO.description,
      canonicalPath: pathname,
    };
  }

  if (segments.length === 2) {
    return {
      ...phaseSeo,
      jsonLd: breadcrumbJsonLd([
        { name: 'Courses', path: '/courses' },
        { name: phaseSeo.title, path: phaseSeo.canonicalPath },
      ]),
    };
  }

  const courseSlug = segments[2];

  if (phase === 'opening' && (courseSlug === 'w' || courseSlug === 'b') && segments.length === 3) {
    const sideTitle = courseSlug === 'w' ? 'White Openings' : 'Black Openings';
    const sideDescription =
      courseSlug === 'w'
        ? 'Browse white opening repertoire courses. Study lines move by move and review with spaced repetition on Endchess.'
        : 'Browse black opening repertoire courses. Study lines move by move and review with spaced repetition on Endchess.';
    return {
      title: sideTitle,
      description: sideDescription,
      canonicalPath: pathname,
      jsonLd: breadcrumbJsonLd([
        { name: 'Courses', path: '/courses' },
        { name: COURSE_PHASE_SEO.opening.title, path: '/courses/opening' },
        { name: sideTitle, path: pathname },
      ]),
    };
  }

  if (phase === 'opening' && courseSlug === 'w' && segments.length >= 4) {
    const collection = parseWhiteCollectionParam(segments[3]);
    if (collection) {
      const collectionTitle = whiteCollectionHubTitle(collection);
      const collectionDescription = `Train ${collectionTitle} course lines. Study them move by move and review with spaced repetition on Endchess.`;
      return {
        title: collectionTitle,
        description: collectionDescription,
        canonicalPath: pathname,
        jsonLd: [
          courseJsonLd(collectionTitle, collectionDescription, pathname),
          breadcrumbJsonLd([
            { name: 'Courses', path: '/courses' },
            { name: COURSE_PHASE_SEO.opening.title, path: '/courses/opening' },
            { name: 'White Openings', path: '/courses/opening/w' },
            { name: collectionTitle, path: pathname },
          ]),
        ],
      };
    }
  }

  if (phase === 'opening' && courseSlug === 'b' && segments.length >= 4) {
    const familySlug = decodeURIComponent(segments[3]!);
    if (isBlackBrowseFamilySlug(familySlug)) {
      const familyTitle = blackFamilyHubTitle(familySlug);
      const familyDescription = `Train ${familyTitle} course lines. Study them move by move and review with spaced repetition on Endchess.`;
      return {
        title: familyTitle,
        description: familyDescription,
        canonicalPath: pathname,
        jsonLd: [
          courseJsonLd(familyTitle, familyDescription, pathname),
          breadcrumbJsonLd([
            { name: 'Courses', path: '/courses' },
            { name: COURSE_PHASE_SEO.opening.title, path: '/courses/opening' },
            { name: 'Black Openings', path: '/courses/opening/b' },
            { name: familyTitle, path: pathname },
          ]),
        ],
      };
    }
  }

  const courseTitle = slugToDisplayTitle(courseSlug);
  if (segments.length >= 4 && segments[3] === 'lessons') {
    return {
      title: `${courseTitle} Lesson`,
      description: `Study a ${courseTitle} course line move by move and review with spaced repetition on Endchess.`,
      canonicalPath: pathname,
      jsonLd: breadcrumbJsonLd([
        { name: 'Courses', path: '/courses' },
        { name: COURSE_PHASE_SEO[phase]?.title ?? phase, path: `/courses/${phase}` },
        { name: courseTitle, path: `/courses/${phase}/${courseSlug}` },
        { name: 'Lesson', path: pathname },
      ]),
    };
  }

  const courseDescription = `Train ${courseTitle} course lines. Study them move by move and review with spaced repetition on Endchess.`;
  return {
    title: courseTitle,
    description: courseDescription,
    canonicalPath: pathname,
    jsonLd: [
      courseJsonLd(courseTitle, courseDescription, pathname),
      breadcrumbJsonLd([
        { name: 'Courses', path: '/courses' },
        { name: COURSE_PHASE_SEO[phase]?.title ?? phase, path: `/courses/${phase}` },
        { name: courseTitle, path: pathname },
      ]),
    ],
  };
};

const getGamesBrowseRouteSeo = (
  pathname: string,
  search: string,
): PageSeo | null => {
  if (isLegacyGamesBrowseQueryUrl(pathname, search)) {
    return {
      title: GAMES_BROWSE_HUB_SEO.title,
      description: GAMES_BROWSE_HUB_SEO.description,
      canonicalPath: pathname,
      noindex: true,
    };
  }

  const parsed = parseGamesBrowsePath(pathname);
  if (!parsed) {
    return null;
  }

  if (parsed.kind === 'hub') {
    return GAMES_BROWSE_HUB_SEO;
  }

  if (parsed.kind === 'openingFamily') {
    const familyTitle = slugToDisplayTitle(parsed.familySlug);
    return {
      title: `${familyTitle} — Master Games`,
      description: `Replay elite ${familyTitle} games from the master database. Study lines move by move and review with spaced repetition on Endchess.`,
      canonicalPath: pathname,
      jsonLd: breadcrumbJsonLd([
        { name: 'Games', path: GAMES_BROWSE_PATH },
        { name: familyTitle, path: pathname },
      ]),
    };
  }

  if (parsed.kind === 'openingVariation') {
    const familyTitle = slugToDisplayTitle(parsed.familySlug);
    const variationTitle = slugToDisplayTitle(parsed.variationSlug);
    const ecoSuffix = parsed.eco ? ` (${parsed.eco.toUpperCase()})` : '';
    return {
      title: `${familyTitle}: ${variationTitle}${ecoSuffix} — Master Games`,
      description: `Replay elite ${familyTitle} ${variationTitle} games from the master database. Study the line move by move and review with spaced repetition on Endchess.`,
      canonicalPath: pathname,
      jsonLd: breadcrumbJsonLd([
        { name: 'Games', path: GAMES_BROWSE_PATH },
        { name: familyTitle, path: `/games/browse/openings/${parsed.familySlug}` },
        { name: `${variationTitle}${ecoSuffix}`, path: pathname },
      ]),
    };
  }

  if (parsed.kind === 'playerOpening') {
    const source = parseCorpusPathSegment(parsed.corpus);
    const playerTitle = formatPlayerSerpDisplayName(parsed.playerSlug, {
      masterStyle: source === 'twic',
    });
    const familyTitle = slugToDisplayTitle(parsed.familySlug);
    const variationTitle = parsed.variationSlug
      ? slugToDisplayTitle(parsed.variationSlug)
      : null;
    const openingTitle = variationTitle
      ? `${familyTitle}: ${variationTitle}`
      : familyTitle;
    const ecoSuffix = parsed.eco ? ` (${parsed.eco.toUpperCase()})` : '';
    const playerPath = `/games/browse/${parsed.corpus}/${parsed.playerSlug}`;
    return {
      title: `${openingTitle}${ecoSuffix} — ${playerTitle}`,
      description: `Replay ${playerTitle} games in the ${openingTitle}. Study them move by move and review with spaced repetition on Endchess.`,
      canonicalPath: pathname,
      jsonLd: breadcrumbJsonLd([
        { name: 'Games', path: GAMES_BROWSE_PATH },
        { name: playerTitle, path: playerPath },
        { name: `${openingTitle}${ecoSuffix}`, path: pathname },
      ]),
    };
  }

  const source = parseCorpusPathSegment(parsed.corpus);
  const playerTitle = formatPlayerSerpDisplayName(parsed.playerSlug, {
    masterStyle: source === 'twic',
  });
  const possessive = formatPlayerSerpPossessive(playerTitle);
  return {
    title: `${playerTitle} Games`,
    description: `Replay ${possessive} games. Study them move by move and review with spaced repetition on Endchess.`,
    canonicalPath: pathname,
    jsonLd: breadcrumbJsonLd([
      { name: 'Games', path: GAMES_BROWSE_PATH },
      { name: playerTitle, path: pathname },
    ]),
  };
};

const getExplorerOpeningsDetailSeo = (pathname: string): PageSeo | null => {
  if (!pathname.startsWith(`${EXPLORER_OPENINGS_HUB_PATH}/`)) {
    return null;
  }

  const segments = pathname
    .slice(EXPLORER_OPENINGS_HUB_PATH.length + 1)
    .split('/')
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));
  if (segments.length !== 2) {
    return {
      ...EXPLORER_OPENINGS_HUB_SEO,
      canonicalPath: pathname,
      noindex: true,
    };
  }

  const [eco, slug] = segments;
  const openingTitle = slugToDisplayTitle(slug);

  return {
    title: explorerOpeningSeoTitle(openingTitle, eco),
    description: explorerOpeningSeoDescription(openingTitle, eco),
    canonicalPath: pathname,
    jsonLd: breadcrumbJsonLd([
      { name: 'Explorer', path: '/explorer' },
      { name: 'Openings', path: EXPLORER_OPENINGS_HUB_PATH },
      { name: openingTitle, path: pathname },
    ]),
  };
};

export const getRouteSeo = (pathname: string, search = ''): PageSeo => {
  if (pathname === '/' || pathname === '/login') {
    return {
      ...HOME_SEO,
      noindex: ROBOTS_NOINDEX || undefined,
      jsonLd: websiteJsonLd(),
    };
  }
  if (pathname === '/subscribe') {
    return { ...SUBSCRIBE_SEO, noindex: ROBOTS_NOINDEX || undefined };
  }
  if (pathname === '/contact') {
    return { ...CONTACT_SEO, noindex: ROBOTS_NOINDEX || undefined };
  }

  const coursesSeo = getCoursesRouteSeo(pathname);
  if (coursesSeo) {
    return {
      ...coursesSeo,
      noindex: ROBOTS_NOINDEX || undefined,
    };
  }

  const gamesBrowseSeo = getGamesBrowseRouteSeo(pathname, search);
  if (gamesBrowseSeo) {
    return {
      ...gamesBrowseSeo,
      noindex: ROBOTS_NOINDEX || gamesBrowseSeo.noindex || undefined,
    };
  }

  if (pathname === EXPLORER_OPENINGS_HUB_PATH) {
    return {
      ...EXPLORER_OPENINGS_HUB_SEO,
      noindex: ROBOTS_NOINDEX || undefined,
    };
  }

  const explorerOpeningSeo = getExplorerOpeningsDetailSeo(pathname);
  if (explorerOpeningSeo) {
    return {
      ...explorerOpeningSeo,
      noindex: ROBOTS_NOINDEX || explorerOpeningSeo.noindex || undefined,
    };
  }

  if (pathname === '/explorer' || pathname.startsWith('/explorer/')) {
    return {
      ...EXPLORER_SEO,
      noindex:
        ROBOTS_NOINDEX || !isIndexableExplorerLanding(pathname, search),
    };
  }

  const privateRoute = isPrivateRoute(pathname);
  let title = SITE_NAME;

  if (pathname === '/settings') title = 'Settings';
  else if (pathname === '/puzzles' || pathname.startsWith('/puzzles/')) title = 'Puzzles';
  else if (pathname === '/review' || pathname.startsWith('/review/')) title = 'Review';
  else if (pathname === '/dashboard' || pathname.startsWith('/dashboard/')) title = 'Dashboard';
  else if (pathname === '/stats' || pathname.startsWith('/stats/')) title = 'Stats';
  else if (pathname === '/games/mine' || pathname.startsWith('/games/mine/'))
    title = 'My Games';
  else if (pathname === '/games') title = 'Games';
  else if (pathname.startsWith('/replay')) title = 'Replay';

  return {
    title,
    description: HOME_SEO.description,
    canonicalPath: resolveCanonicalPath(pathname),
    noindex: ROBOTS_NOINDEX || privateRoute,
  };
};
